import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'src/store';
import { runtimeConfigs } from 'src/config/runtimeConfigs';
import {
  getAccessToken,
  loginWithCodeVerification,
  loginWithUserNameAndPassword,
  logout as logoutService,
} from '../services/authService';
import { loadAllDireitos } from '../services/direitosService';
import { UserDTO } from '../types/dtos/auth';
import {
  buscarAplicacoesWeb,
  verificarConectividade,
  buscarDadosSobre,
} from '../services/ambienteService';
import { AplicacoesWebDTO, SobreDTO } from '../types/dtos/genericos';
import { verificarAutenticacaoA2F } from '../services/pessoaService';

export interface AccountState {
  user: UserDTO | null;
  direitos: string[];
  dadosPortais: AplicacoesWebDTO | null;
  tipoLogin: 'cigam' | 'contato';
  possuiA2F?: boolean | null;
  possuiConectividade?: boolean;
  dadosSobre: SobreDTO | null;
}

const initialState: AccountState = {
  user: null,
  direitos: [],
  dadosPortais: null,
  tipoLogin: 'cigam',
  possuiA2F: false,
  possuiConectividade: false,
  dadosSobre: null,
};

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    clearAccount(state: AccountState) {
      state.user = null;
      state.dadosPortais = null;
      state.direitos = [];
      state.possuiA2F = false;
      state.possuiConectividade = false;
    },
    setAccount(state: AccountState, action: PayloadAction<AccountState>) {
      const {
        user,
        tipoLogin,
        dadosPortais,
        possuiA2F,
        possuiConectividade,
        dadosSobre,
      } = action.payload;

      state.user = user;
      state.dadosPortais = dadosPortais;
      state.tipoLogin = tipoLogin || 'cigam';
      state.possuiA2F = possuiA2F;
      state.possuiConectividade = possuiConectividade;
      state.dadosSobre = dadosSobre;
    },
  },
});

export const { reducer } = slice;

export const { setAccount } = slice.actions;

export const login =
  (
    userName: string,
    password: string,
    portal: string,
    codigoVerificacao?: string,
    perguntaSecreta?: number,
    respostaSecreta?: string
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.clearAccount());
    const idPaiControleAcesso = Number.parseFloat(
      localStorage.getItem('idPaiControleAcesso') ?? '0'
    );

    const user = await loginWithUserNameAndPassword(
      userName,
      password,
      portal,
      codigoVerificacao,
      perguntaSecreta,
      respostaSecreta,
      idPaiControleAcesso
    );

    const userStorage = localStorage.getItem('user');
    const userData = userStorage ? JSON.parse(userStorage) : '';

    if (
      userData.CodigoMensagem &&
      (userData.CodigoMensagem === '440' || userData.CodigoMensagem === '439')
    ) {
      const direitos: any[] = [];

      dispatch(
        slice.actions.setAccount({
          user: userData,
          tipoLogin: 'cigam',
          direitos,
          dadosPortais: null,
          possuiA2F: false,
          possuiConectividade: false,
          dadosSobre: null,
        })
      );
    } else {
      const direitos = await loadAllDireitos();
      const hash = getAccessToken();
      const enviroment = await runtimeConfigs();
      const dadosPortais = await buscarAplicacoesWeb(
        `${enviroment.API_URL}`.includes('https')
      );
      const dadosSobre = await buscarDadosSobre();
      let possuiConectividade: boolean;
      possuiConectividade = false;
      const dadosCia = dadosPortais.data.Aplicacoes.filter(x =>
        x.Endereco.includes('cia')
      )[0];
      if (dadosCia !== null) {
        const endereco = dadosCia.Endereco.concat('?cigam=')
          .concat(enviroment.API_URL)
          .concat(hash ? '&hash='.concat(hash) : '');

        const verificaConectividade = await verificarConectividade(
          endereco,
          true
        );
        possuiConectividade = verificaConectividade.data.IsAvailable;
      }

      const verificaA2F = await verificarAutenticacaoA2F(user[0].Codigo);
      const possuiA2F = verificaA2F.data.PossuiAutenticacaoA2F;

      dispatch(
        slice.actions.setAccount({
          user: user[0],
          tipoLogin: 'cigam',
          direitos,
          dadosPortais,
          possuiA2F,
          possuiConectividade,
          dadosSobre,
        })
      );
    }
  };

export const loginContato =
  (
    portal: string,
    codePerson: string,
    sequence: number,
    codeVerification: string
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.clearAccount());

    const user = await loginWithCodeVerification(
      portal,
      codePerson,
      sequence,
      codeVerification
    );

    const direitos = await loadAllDireitos();

    dispatch(
      slice.actions.setAccount({
        user: user[0],
        tipoLogin: 'contato',
        direitos,
        dadosPortais: null,
        possuiA2F: false,
        possuiConectividade: false,
        dadosSobre: null,
      })
    );
  };

export const logout =
  (): AppThunk =>
  (dispatch): void => {
    logoutService();

    dispatch(slice.actions.clearAccount());
  };
